import { getDesignTheme, designPx as px } from './idCardTheme';
import { getCpRawatLogoUrl } from './studentCardUrl';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const fmtIdDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

function fontLink(design) {
  const T = getDesignTheme(design);
  return `<link rel="stylesheet" href="${T.googleFonts}" />`;
}

function cardBaseCss(w, h, portrait, design = 'saffron') {
  const T = getDesignTheme(design);
  const photo = portrait ? px(72) : px(56);
  const th = portrait ? px(96) : px(68);
  return `
${fontLink(design)}
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  .id-card {
    width: ${w}px; height: ${h}px;
    font-family: ${T.fontStack};
    background: ${T.white};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
    border-radius: 0px;
    box-shadow: 0 8px 24px rgba(15,23,42,0.14);
  }
  .id-bg-layer { position: absolute; inset: 0; z-index: 0; pointer-events: none; }
  .id-bg-layer img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .id-card > *:not(.id-bg-layer) { position: relative; z-index: 1; }

  .id-header {
    flex: 0 0 ${th}px;
    background: ${T.gradient};
    color: ${T.white};
    text-align: center;
    padding: ${portrait ? `${px(12)}px ${px(10)}px ${px(36)}px` : `${px(8)}px ${px(8)}px ${px(28)}px`};
    position: relative;
    overflow: hidden;
  }
  .id-header::after {
    content: '';
    position: absolute;
    left: -5%; right: -5%; bottom: -1px;
    height: ${px(28)}px;
    background: ${T.primaryLight};
    opacity: 0.55;
    border-radius: 50% 50% 0 0 / 100% 100% 0 0;
    transform: translateY(${px(12)}px);
  }
  .id-header-wave {
    position: absolute;
    left: 0; right: 0; bottom: -2px;
    height: ${px(22)}px;
    background: ${T.white};
    border-radius: 50% 50% 0 0 / 100% 100% 0 0;
  }
  .id-header-inner { position: relative; z-index: 2; }
  .id-header .logo {
    max-height: ${portrait ? px(28) : px(20)}px;
    max-width: ${portrait ? px(56) : px(44)}px;
    margin: 0 auto ${px(4)}px;
    display: block;
    object-fit: contain;
    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));
  }
  .id-header h1 {
    font-size: ${portrait ? px(9.5) : px(7.5)}px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.35px;
    line-height: 1.15;
    text-shadow: 0 1px 2px rgba(0,0,0,0.15);
  }
  .id-header .slogan {
    font-size: ${portrait ? px(5.5) : px(4.5)}px;
    font-weight: 500;
    margin-top: ${px(3)}px;
    opacity: 0.95;
    letter-spacing: 0.2px;
  }

  .id-main { flex: 1; min-height: 0; display: flex; flex-direction: column; background: ${T.white}; }
  .id-photo-wrap {
    text-align: center;
    margin-top: ${portrait ? -px(34) : -px(26)}px;
    flex-shrink: 0;
    z-index: 3;
  }
  .id-photo-ring {
    width: ${photo + px(10)}px; height: ${photo + px(10)}px;
    border-radius: 50%;
    background: ${T.gradient};
    padding: ${px(4)}px;
    margin: 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px ${T.ringShadow};
  }
  .id-photo-ring img, .id-photo-ring .ph {
    width: ${photo}px; height: ${photo}px;
    border-radius: 50%;
    object-fit: cover;
    background: #e2e8f0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; color: #64748b;
    font-size: ${portrait ? px(22) : px(16)}px;
    border: ${px(2)}px solid ${T.white};
  }

  .id-details {
    flex: 1;
    padding: ${portrait ? `${px(8)}px ${px(14)}px ${px(4)}px` : `${px(6)}px ${px(10)}px ${px(2)}px`};
    font-size: ${portrait ? px(7) : px(6)}px;
    color: ${T.navy};
    min-height: 0;
  }
  .id-details .row {
    display: grid;
    grid-template-columns: ${portrait ? `${px(58)}px` : `${px(50)}px`} ${px(6)}px 1fr;
    align-items: baseline;
    margin-bottom: ${portrait ? px(3) : px(2)}px;
    line-height: 1.35;
  }
  .id-details .lbl { font-weight: 600; color: ${T.navyMuted}; font-size: ${portrait ? px(6.5) : px(5.5)}px; }
  .id-details .colon { font-weight: 700; color: ${T.navyMuted}; text-align: center; }
  .id-details .val { font-weight: 600; color: ${T.navy}; word-break: break-word; }
  .id-details .row.em .val { color: ${T.primaryDark}; }

  .id-footer-stack {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    margin-top: auto;
    position: relative;
    z-index: 2;
  }
  .id-footer-visual {
    position: relative;
    min-height: ${portrait ? px(26) : px(20)}px;
    overflow: hidden;
  }
  .id-footer-visual.has-image {
    min-height: ${portrait ? px(38) : px(30)}px;
  }
  .id-footer-visual .id-footer-media-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center bottom;
    display: block;
    z-index: 0;
    opacity: 1;
  }
  .id-footer-overlay {
    position: relative;
    z-index: 2;
    min-height: ${portrait ? px(28) : px(22)}px;
    display: flex;
    align-items: flex-end;
    padding: ${portrait ? `${px(3)}px ${px(10)}px ${px(2)}px` : `${px(2)}px ${px(8)}px ${px(2)}px`};
    box-sizing: border-box;
  }
  .id-footer-visual.mode-front .id-footer-overlay {
    justify-content: flex-end;
  }
  .id-footer-visual.mode-back .id-footer-overlay {
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding-bottom: ${portrait ? px(4) : px(3)}px;
  }
  .id-sig-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    max-width: 50%;
    position: relative;
    z-index: 3;
  }
  .id-sig-box img {
    max-height: ${portrait ? px(24) : px(18)}px;
    max-width: ${portrait ? px(80) : px(64)}px;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 1px 2px rgba(255,255,255,0.85));
  }
  .id-sig-box .sig-ph {
    width: ${portrait ? px(60) : px(48)}px;
    height: ${portrait ? px(14) : px(11)}px;
    border-bottom: ${px(1.5)}px solid #64748b;
    margin-bottom: ${px(2)}px;
    background: rgba(255,255,255,0.5);
  }
  .id-sig-box .sig-lbl {
    font-size: ${portrait ? px(5.5) : px(5)}px;
    font-weight: 700;
    color: ${T.navy};
    text-transform: uppercase;
    letter-spacing: ${px(0.4)}px;
    text-shadow: 0 ${px(1)}px 0 rgba(255,255,255,0.9);
  }

  .id-footer-bar {
    flex-shrink: 0;
    background: ${T.gradient};
    color: ${T.white};
    min-height: ${portrait ? px(24) : px(18)}px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${px(4)}px;
    padding: ${portrait ? `${px(5)}px ${px(8)}px` : `${px(4)}px ${px(6)}px`};
    position: relative;
    overflow: hidden;
    z-index: 4;
  }
  .id-footer-bar .id-footer-bg {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: cover; z-index: 0;
  }
  .id-footer-bar .id-footer-text {
    position: relative; z-index: 1;
    font-size: ${portrait ? px(5) : px(4.5)}px;
    line-height: 1.25;
    font-weight: 500;
    text-align: center;
    text-shadow: 0 ${px(1)}px ${px(2)}px rgba(0,0,0,0.35);
    display: flex; align-items: flex-start; gap: ${px(3)}px;
  }
  .id-footer-bar .pin { flex-shrink: 0; font-size: ${portrait ? px(7) : px(6)}px; line-height: 1; }

  .id-back-head {
    flex-shrink: 0;
    background: ${T.gradient};
    color: ${T.white};
    font-size: ${portrait ? px(7.5) : px(6.5)}px;
    font-weight: 800;
    text-align: center;
    padding: ${px(6)}px ${px(8)}px;
    text-transform: uppercase;
    letter-spacing: ${px(0.5)}px;
  }
  .id-back-body {
    flex: 1; min-height: 0;
    padding: ${portrait ? `${px(8)}px ${px(10)}px ${px(4)}px` : `${px(6)}px ${px(8)}px ${px(3)}px`};
    display: flex; flex-direction: column;
    font-size: ${portrait ? px(6) : px(5.5)}px;
    color: ${T.navy};
  }
  .id-back-terms { flex-shrink: 0; margin-bottom: ${px(6)}px; }
  .id-term {
    display: flex; align-items: flex-start; gap: ${px(6)}px;
    margin-bottom: ${px(4)}px; line-height: 1.4;
    color: ${T.navyMuted};
  }
  .id-term .bullet {
    width: ${px(5)}px; height: ${px(5)}px; margin-top: ${px(3)}px;
    background: ${T.primary};
    flex-shrink: 0;
    border-radius: ${px(1)}px;
  }
  .id-divider {
    height: ${px(1)}px;
    background: ${T.grayBorder};
    margin: ${px(4)}px 0 ${px(6)}px;
  }
  .id-back-center {
    flex: 1;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center;
    gap: ${portrait ? px(4) : px(3)}px;
    min-height: 0;
  }
  .id-contact { line-height: 1.45; width: 100%; color: ${T.navy}; }
  .id-contact .lbl { font-weight: 700; color: ${T.navy}; }
  .id-qr { display: flex; justify-content: center; margin: ${portrait ? `${px(4)}px 0` : `${px(3)}px 0`}; }
  .id-qr img {
    width: ${portrait ? px(62) : px(48)}px;
    height: ${portrait ? px(62) : px(48)}px;
    object-fit: contain;
    display: block;
  }
  .id-dates-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${portrait ? px(10) : px(8)}px;
    width: 100%;
    margin-top: ${px(2)}px;
  }
  .id-date-block { text-align: center; }
  .id-date-block .dlbl {
    font-size: ${portrait ? px(5.5) : px(5)}px;
    font-weight: 700;
    color: ${T.primary};
    text-transform: uppercase;
  }
  .id-date-block .dval {
    font-size: ${portrait ? px(6.5) : px(6)}px;
    font-weight: 700;
    color: ${T.navy};
  }
  .id-date-sep { width: ${px(1)}px; height: ${px(22)}px; background: #cbd5e1; }

  .id-managed {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${px(2)}px;
    max-width: 92%;
    position: relative;
    z-index: 3;
  }
  .id-managed-lbl {
    font-size: ${portrait ? px(5) : px(4.5)}px;
    font-weight: 700;
    color: ${T.primaryDark};
    text-transform: uppercase;
    letter-spacing: ${px(0.5)}px;
    text-shadow: 0 ${px(1)}px 0 rgba(255,255,255,0.95);
  }
  .id-managed img {
    max-height: ${portrait ? px(30) : px(24)}px;
    max-width: ${portrait ? px(88) : px(72)}px;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    margin: 0 auto;
    filter: drop-shadow(0 ${px(1)}px ${px(2)}px rgba(255,255,255,0.85));
  }
  .id-managed .org-name {
    font-size: ${portrait ? px(5.5) : px(5)}px;
    font-weight: 600;
    color: ${T.navy};
    line-height: 1.25;
    max-width: 100%;
    padding: ${px(1)}px ${px(5)}px;
    border-radius: ${px(3)}px;
    background: rgba(255,255,255,0.72);
    box-shadow: 0 0 0 ${px(1)}px rgba(255,255,255,0.35);
  }
  .id-managed .logo-ph {
    width: ${portrait ? px(44) : px(36)}px;
    height: ${portrait ? px(22) : px(16)}px;
    margin: 0 auto;
    border: ${px(1)}px dashed #cbd5e1;
    border-radius: ${px(4)}px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${px(7)}px;
    color: #94a3b8;
    background: rgba(255,255,255,0.65);
  }
  .id-back-body .id-back-center {
    padding-bottom: ${portrait ? px(2) : px(1)}px;
  }

  .id-card.has-custom-bg .id-details {
    background: rgba(255,255,255,0.9);
    border-radius: ${px(6)}px;
    margin: 0 ${px(8)}px;
  }
  .id-card.landscape .id-header {
    flex: 0 0 ${px(58)}px;
    padding: ${px(6)}px ${px(8)}px ${px(22)}px;
  }
  .id-card.landscape .id-header h1 {
    font-size: ${px(7)}px;
    line-height: 1.1;
  }
  .id-card.landscape .id-body-row {
    display: flex;
    flex: 1;
    min-height: 0;
    align-items: center;
    gap: ${px(6)}px;
    padding: 0 ${px(6)}px;
  }
  .id-card.landscape .id-side-photo {
    width: 36%;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .id-card.landscape .id-photo-wrap {
    margin-top: 0;
    padding-top: ${px(4)}px;
  }
  .id-card.landscape .id-side-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    justify-content: center;
  }
  .id-card.landscape .id-details {
    padding: ${px(4)}px ${px(6)}px ${px(2)}px;
  }
  .id-card.landscape .id-details .row {
    grid-template-columns: ${px(62)}px ${px(6)}px 1fr;
    align-items: start;
    margin-bottom: ${px(3)}px;
    min-height: ${px(10)}px;
    line-height: 1.25;
  }
  .id-card.landscape .id-details .lbl {
    font-size: ${px(5.5)}px;
    line-height: 1.2;
    white-space: normal;
  }
  .id-card.landscape .id-details .val {
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .id-card.landscape .id-back-body {
    padding: ${px(4)}px ${px(8)}px ${px(2)}px;
  }
  .id-card.landscape .id-back-terms {
    margin-bottom: ${px(3)}px;
  }
  .id-card.landscape .id-term {
    margin-bottom: ${px(2)}px;
    line-height: 1.3;
  }
  .id-card.landscape .id-divider {
    margin: ${px(2)}px 0 ${px(3)}px;
  }
  .id-card.landscape .id-back-center {
    gap: ${px(2)}px;
  }
  .id-card.landscape .id-qr {
    margin: ${px(2)}px 0;
  }
  .id-card.landscape .id-qr img {
    width: ${px(42)}px;
    height: ${px(42)}px;
  }
</style>`;
}

function bgLayer(url) {
  if (!url) return '';
  return `<div class="id-bg-layer"><img src="${esc(url)}" alt="" crossorigin="anonymous" /></div>`;
}

function headerBlock(d) {
  const logo = d.logoUrl
    ? `<img class="logo" src="${esc(d.logoUrl)}" alt="" crossorigin="anonymous" />`
    : '';
  return `<div class="id-header"><div class="id-header-inner">${logo}<h1>${esc(d.schoolName)}</h1><p class="slogan">${esc(d.slogan)}</p></div><div class="id-header-wave"></div></div>`;
}

function detailsRows(d, portrait) {
  const rows = [
    ['Reg No', d.admissionNo, false],
    ['Student ID', d.studentId, false],
    ['Student Name', d.studentName, false],
    ['Father/ Guardian', d.fatherName, false],
    ['Class', d.classLabel, false],
    ['Mobile number', d.emergencyPhone, true],
  ];
  return rows
    .map(([lbl, val, em]) =>
      `<div class="row${em ? ' em' : ''}"><span class="lbl">${lbl}</span><span class="colon">:</span><span class="val">${esc(val)}</span></div>`
    )
    .join('');
}

function signatureOverlayContent(d) {
  const img = d.principalSignatureUrl
    ? `<img src="${esc(d.principalSignatureUrl)}" alt="Signature" crossorigin="anonymous" />`
    : '<div class="sig-ph"></div>';
  return `<div class="id-sig-box">${img}<span class="sig-lbl">Principal</span></div>`;
}

function managedOverlayContent(d) {
  const logoUrl = getCpRawatLogoUrl();
  const orgName = esc(d.managedOrgName || 'CP Rawat Softwares');
  const img = logoUrl
    ? `<img src="${esc(logoUrl)}" alt="${orgName}" crossorigin="anonymous" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'" /><div class="logo-ph" style="display:none">Logo</div>`
    : `<div class="logo-ph">Logo</div>`;
  return `<div class="id-managed"><span class="id-managed-lbl">Managed by</span>${img}<div class="org-name">${orgName}</div></div>`;
}

function footerBar(d) {
  const addr = esc(d.schoolAddress || d.schoolName);
  return `<div class="id-footer-bar"><div class="id-footer-text"><span class="pin">📍</span><span>${addr}</span></div></div>`;
}

function footerStack(d, mode = 'front') {
  const hasImg = Boolean(d.footerBgUrl);
  const visualClass = `id-footer-visual mode-${mode}${hasImg ? ' has-image' : ''}`;
  const mediaImg = hasImg
    ? `<img class="id-footer-media-img" src="${esc(d.footerBgUrl)}" alt="" crossorigin="anonymous" />`
    : '';
  const overlay = mode === 'back' ? managedOverlayContent(d) : signatureOverlayContent(d);
  return `<div class="id-footer-stack">
  <div class="${visualClass}">
    ${mediaImg}
    <div class="id-footer-overlay">${overlay}</div>
  </div>
  ${footerBar(d)}
</div>`;
}

export function buildFrontHtml(d, widthPx, heightPx, portrait = true, design = 'saffron') {
  const hasBg = Boolean(d.frontBgUrl);
  const photo = d.photoUrl
    ? `<img src="${esc(d.photoUrl)}" alt="" crossorigin="anonymous" />`
    : `<span class="ph">${esc(d.initials)}</span>`;

  const body = `
    ${headerBlock(d)}
    <div class="id-main">
      <div class="id-photo-wrap"><div class="id-photo-ring">${photo}</div></div>
      <div class="id-details">${detailsRows(d, portrait)}</div>
    </div>
    ${footerStack(d, 'front')}
  `;

  if (!portrait) {
    return `${cardBaseCss(widthPx, heightPx, false, design)}
<div class="id-card landscape design-${design}${hasBg ? ' has-custom-bg' : ''}">
  ${bgLayer(d.frontBgUrl)}
  ${headerBlock(d)}
  <div class="id-body-row">
    <div class="id-side-photo">
      <div class="id-photo-wrap"><div class="id-photo-ring">${photo}</div></div>
    </div>
    <div class="id-side-details">
      <div class="id-details">${detailsRows(d, false)}</div>
    </div>
  </div>
  ${footerStack(d, 'front')}
</div>`;
  }

  return `${cardBaseCss(widthPx, heightPx, true, design)}
<div class="id-card design-${design}${hasBg ? ' has-custom-bg' : ''}">
  ${bgLayer(d.frontBgUrl)}
  ${body}
</div>`;
}

export function buildBackHtml(d, widthPx, heightPx, portrait = true, design = 'saffron') {
  const hasBg = Boolean(d.backBgUrl);
  const qr = d.qrDataUrl ? `<div class="id-qr"><img src="${esc(d.qrDataUrl)}" alt="QR" /></div>` : '';

  return `${cardBaseCss(widthPx, heightPx, portrait, design)}
<div class="id-card${!portrait ? ' landscape' : ''} design-${design}${hasBg ? ' has-custom-bg' : ''}">
  ${bgLayer(d.backBgUrl)}
  <div class="id-back-head">Terms and Conditions</div>
  <div class="id-back-body">
    <div class="id-back-terms">
      <p class="id-term"><span class="bullet"></span><span>${esc(d.termsText)}</span></p>
      <p class="id-term"><span class="bullet"></span><span>${esc(d.termsText2)}</span></p>
    </div>
    <div class="id-divider"></div>
    <div class="id-back-center">
      <div class="id-contact"><span class="lbl">Phone</span> : ${esc(d.schoolPhone)}</div>
      <div class="id-contact"><span class="lbl">Mail</span> : ${esc(d.schoolEmail)}</div>
      <div class="id-contact"><span class="lbl">Website</span> : ${esc(d.schoolWebsite)}</div>
      ${qr}
      <div class="id-dates-row">
        <div class="id-date-block"><div class="dlbl">Joined Date</div><div class="dval">${fmtIdDate(d.joined)}</div></div>
        <div class="id-date-sep"></div>
        <div class="id-date-block"><div class="dlbl">Expire Date</div><div class="dval">${fmtIdDate(d.expire)}</div></div>
      </div>
    </div>
  </div>
  ${footerStack(d, 'back')}
</div>`;
}
